import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
};

const getProvider = (walletType) => {
  if (walletType === 'okx') {
    return window.okxwallet;
  }
  return window.ethereum;
};

export const WalletProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [balance, setBalance] = useState('0');
  const [chainId, setChainId] = useState(null);
  const [walletType, setWalletType] = useState(null);

  const disconnect = useCallback(() => {
    setIsConnected(false);
    setWalletAddress('');
    setBalance('0');
    setChainId(null);
    setWalletType(null);
    console.log('Wallet disconnected');
  }, []);

  const updateWalletInfo = useCallback(async (address, type) => {
    try {
      const provider = getProvider(type);
      if (!provider) {
        throw new Error('Provider not available');
      }

      console.log('=== UPDATING WALLET INFO ===');
      console.log('Address:', address);
      console.log('Wallet Type:', type);

      setIsConnected(true);
      setWalletAddress(address);
      setWalletType(type);
      
      const chain = await provider.request({ method: 'eth_chainId' });
      setChainId(chain);
      console.log('Connected to chain ID:', chain);
      
      const chainNames = {
        '0x1': 'Ethereum Mainnet',
        '0xa4b1': 'Arbitrum One',
        '0xa': 'Optimism',
        '0x2105': 'Base'
      };
      console.log('Network:', chainNames[chain] || 'Unknown Network');
      
      let attempts = 0;
      let bal = '0x0';
      
      while (attempts < 3) {
        try {
          bal = await provider.request({
            method: 'eth_getBalance',
            params: [address, 'latest']
          });
          
          console.log(`Balance attempt ${attempts + 1}:`, bal);
          
          if (bal && bal !== '0x0' || attempts === 2) {
            break;
          }
          
          await new Promise(resolve => setTimeout(resolve, 500));
          attempts++;
        } catch (err) {
          console.error(`Balance check attempt ${attempts + 1} failed:`, err);
          attempts++;
          
          if (attempts < 3) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      const balanceInEth = parseInt(bal, 16) / 1e18;
      const formattedBalance = balanceInEth.toFixed(4);
      
      console.log('Raw balance (hex):', bal);
      console.log('Balance (wei):', parseInt(bal, 16));
      console.log('Balance (ETH):', balanceInEth);
      console.log('Formatted balance:', formattedBalance);
      
      setBalance(formattedBalance);
      
      console.log('=== WALLET INFO UPDATED ===');
      console.log('Final state:', {
        address: address,
        chain: chain,
        chainName: chainNames[chain] || 'Unknown',
        balance: formattedBalance + ' ETH',
        walletType: type
      });
      console.log('===========================');
    } catch (err) {
      console.error('Error updating wallet info:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
    }
  }, []);

  const checkConnection = useCallback(async () => {
    try {
      console.log('Checking wallet connection...');
      
      if (window.okxwallet) {
        console.log('OKX Wallet detected');
        const accounts = await window.okxwallet.request({ method: 'eth_accounts' });
        console.log('OKX accounts:', accounts);
        
        if (accounts.length > 0) {
          console.log('Found existing OKX connection');
          await updateWalletInfo(accounts[0], 'okx');
          return;
        }
      }

      if (window.ethereum) {
        console.log('MetaMask detected');
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        console.log('MetaMask accounts:', accounts);
        
        if (accounts.length > 0) {
          console.log('Found existing MetaMask connection');
          await updateWalletInfo(accounts[0], 'metamask');
          return;
        }
      }

      console.log('No existing wallet connection found');
      disconnect();
    } catch (err) {
      console.error('Error checking wallet connection:', err);
    }
  }, [updateWalletInfo, disconnect]);

  const handleAccountsChanged = useCallback((accounts) => {
    console.log('Accounts changed:', accounts);
    
    if (accounts.length === 0) {
      console.log('No accounts, disconnecting');
      disconnect();
    } else if (walletType) {
      console.log('Updating to new account');
      updateWalletInfo(accounts[0], walletType);
    }
  }, [disconnect, updateWalletInfo, walletType]);

  const handleChainChanged = useCallback((newChainId) => {
    console.log('Chain changed to:', newChainId);
    console.log('Refreshing wallet info...');
    checkConnection();
  }, [checkConnection]);

  useEffect(() => {
    console.log('WalletContext initializing...');
    checkConnection();
    
    if (window.ethereum) {
      console.log('Setting up MetaMask event listeners');
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    if (window.okxwallet) {
      console.log('Setting up OKX Wallet event listeners');
      window.okxwallet.on('accountsChanged', handleAccountsChanged);
      window.okxwallet.on('chainChanged', handleChainChanged);
    }

    return () => {
      console.log('Cleaning up event listeners');
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
      if (window.okxwallet) {
        window.okxwallet.removeListener('accountsChanged', handleAccountsChanged);
        window.okxwallet.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, [checkConnection, handleAccountsChanged, handleChainChanged]);

  const connect = async (type = 'metamask') => {
    try {
      console.log(`Attempting to connect ${type} wallet...`);
      
      const provider = getProvider(type);
      
      if (!provider) {
        const walletName = type === 'okx' ? 'OKX Wallet' : 'MetaMask';
        throw new Error(`${walletName} not installed`);
      }

      console.log('Requesting accounts...');
      const accounts = await provider.request({ 
        method: 'eth_requestAccounts' 
      });

      console.log('Accounts received:', accounts);

      if (accounts.length > 0) {
        await updateWalletInfo(accounts[0], type);
        console.log('Connection successful!');
        return true;
      }
    } catch (err) {
      console.error('Error connecting wallet:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code
      });
      throw err;
    }
  };

  const refreshBalance = useCallback(async () => {
    if (!isConnected || !walletType) {
      console.log('Cannot refresh: wallet not connected');
      return;
    }

    console.log('Manually refreshing balance...');
    await updateWalletInfo(walletAddress, walletType);
  }, [isConnected, walletType, walletAddress, updateWalletInfo]);

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const value = {
    isConnected,
    walletAddress,
    balance,
    chainId,
    walletType,
    connect,
    disconnect,
    formatAddress,
    refreshBalance,
    getProvider: () => getProvider(walletType)
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};